import { StoryPoint } from '../types/StoryPoint';
import { PlayerEstimate } from '../features/EstimationResult';

// Cards that prevent alignment score calculation (matches backend NO_ALIGNMENT_CARDS)
const NO_ALIGNMENT_CARDS = ['?'];

// Non-calculable cards that should be excluded from alignment calculation (matches backend NON_CALCULABLE_CARDS)
// Note: Backend includes "?", "☕", "☕️", "♾️" but we only need to handle "?" and "☕" in frontend
const NON_CALCULABLE_CARDS = ['?', '☕'];

// Convert story point to numeric value for calculation
function storyPointToNumber(storyPoint: StoryPoint): number | null {
    const str = String(storyPoint);
    if (NON_CALCULABLE_CARDS.includes(str)) {
        return null;
    }
    if (str === '1/2') {
        return 0.5;
    }
    const num = parseFloat(str);
    return isNaN(num) ? null : num;
}

// Extract numeric estimates from player estimates (matches backend ExtractEstimatesForRoom)
function extractEstimates(estimates: PlayerEstimate[]): number[] {
    return estimates
        .map(e => e.storyPoint ? storyPointToNumber(e.storyPoint) : null)
        .filter((e): e is number => e !== null);
}

/**
 * Calculate alignment score from player estimates
 * Matches the backend algorithm in GameInsightService.CalculateAlignmentScore
 * 
 * Backend logic:
 * 1. First checks if any NO_ALIGNMENT_CARDS ("?") exist - if so, returns -1
 * 2. Then extracts estimates filtering out NON_CALCULABLE_CARDS
 * 3. Calculates score using logarithmic scaling
 * 
 * @param estimates Array of player estimates
 * @returns Alignment score (0-100) or -1 if cannot be calculated
 */
export function calculateAlignmentScore(estimates: PlayerEstimate[]): number {
    // Step 1: Check for NO_ALIGNMENT_CARDS (matches backend CalculateAlignmentScore(Room))
    const hasNoAlignmentCard = estimates.some(e => 
        e.storyPoint && NO_ALIGNMENT_CARDS.includes(String(e.storyPoint))
    );
    if (hasNoAlignmentCard) {
        return -1;
    }
    
    // Step 2: Extract numeric estimates (matches backend ExtractEstimatesForRoom)
    const numericEstimates = extractEstimates(estimates);
    
    // Step 3: Calculate score (matches backend CalculateAlignmentScore(double[]))
    if (numericEstimates.length === 0) {
        return -1;
    }
    
    const minEstimate = Math.min(...numericEstimates);
    const maxEstimate = Math.max(...numericEstimates);
    const maxDeviation = maxEstimate - minEstimate;
    const avgEstimate = numericEstimates.reduce((sum, val) => sum + val, 0) / numericEstimates.length;
    
    // Use logarithmic scaling to penalize larger deviations more heavily
    // This ensures that deviations between lower estimates get higher scores
    // than deviations between higher estimates
    const normalizedDeviation = maxDeviation / (avgEstimate + 1); // +1 to avoid division by zero
    const logPenalty = Math.log(normalizedDeviation + 1);
    
    const alignmentScore = Math.max(0, (1 - logPenalty / Math.log(2)) * 100);
    
    return Math.round(alignmentScore);
}


